import React, { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { HelpCircle, ChevronDown, ChevronUp, Search, Mail, Phone, MessageSquare, FileText, Users, Settings, CreditCard, Shield, Send } from 'lucide-react';
import { toast } from 'sonner';

const HelpCenter = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    question: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const helpCategories = [
    {
      icon: Users,
      title: 'Getting Started',
      description: 'Learn the basics of HRMQore',
      articles: 12
    },
    {
      icon: Settings,
      title: 'Account Settings',
      description: 'Manage your account and preferences',
      articles: 8
    },
    {
      icon: CreditCard,
      title: 'Billing & Payments',
      description: 'Subscription and payment information',
      articles: 6
    },
    {
      icon: FileText,
      title: 'HR Management',
      description: 'Employee records and management',
      articles: 15
    },
    {
      icon: MessageSquare,
      title: 'Communication',
      description: 'Messaging and notifications',
      articles: 9
    },
    {
      icon: Shield,
      title: 'Security & Privacy',
      description: 'Data protection and compliance',
      articles: 7
    }
  ];

  const faqs = [
    {
      question: 'How do I add a new employee to the system?',
      answer: 'To add a new employee, navigate to the Employee module from the sidebar, click on "Add Employee" button, fill in the required information including personal details, job information, and department assignment, then click "Save". The employee will receive an email invitation to set up their account.'
    },
    {
      question: 'How does the leave management system work?',
      answer: 'HRMQore\'s leave management allows employees to request time off through their self-service portal. Managers receive notifications and can approve or reject requests. The system automatically tracks leave balances, accruals, and generates reports. You can configure different leave types and policies based on your organization\'s requirements.'
    },
    {
      question: 'Can I integrate HRMQore with my existing payroll system?',
      answer: 'Yes, HRMQore offers integration capabilities with various payroll systems. We support direct integrations with popular payroll providers and also offer API access for custom integrations. Contact our support team for specific integration requirements.'
    },
    {
      question: 'How secure is my company\'s data on HRMQore?',
      answer: 'We take data security very seriously. HRMQore uses bank-grade encryption (AES-256) for data at rest and in transit. We are compliant with NDPR (Nigeria Data Protection Regulation), and our infrastructure is hosted on secure cloud servers with regular security audits and penetration testing.'
    },
    {
      question: 'What support options are available?',
      answer: 'We offer multiple support channels including email support (24-48 hour response), live chat during business hours, phone support for enterprise customers, and a comprehensive knowledge base. Enterprise plans also include a dedicated account manager and priority support.'
    },
    {
      question: 'Can I customize the platform to match my company\'s branding?',
      answer: 'Yes, HRMQore offers white-labeling options for Business and Enterprise plans. You can customize the logo, color scheme, email templates, and even use a custom domain. Contact our sales team for more information on branding options.'
    },
    {
      question: 'How do I generate reports in HRMQore?',
      answer: 'Navigate to the Reports & Analytics module from the sidebar. You can choose from pre-built reports or create custom reports using our report builder. Reports can be exported to Excel, PDF, or CSV formats. You can also schedule automated reports to be sent to specific email addresses.'
    },
    {
      question: 'What happens to my data if I cancel my subscription?',
      answer: 'Upon cancellation, your data is retained for 30 days, during which you can export all your information. After 30 days, data is permanently deleted from our servers. We recommend exporting your data before cancellation. Enterprise customers can request extended data retention periods.'
    }
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: 'Help Center Inquiry',
          phone: '',
          subject: 'Help Center Question',
          message: formData.question,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Your question has been submitted. We\'ll get back to you soon!');
        setFormData({ name: '', email: '', question: '' });
      } else {
        toast.error('Failed to submit your question. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0066FF 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #0066FF 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="absolute top-10 right-10 w-32 md:w-64 h-32 md:h-64 bg-[#0066FF]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-40 md:w-80 h-40 md:h-80 bg-[#0052CC]/20 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Badge className="bg-white/10 text-white hover:bg-white/20 border-0 mb-4">
            <HelpCircle className="w-4 h-4 mr-2" />
            Support
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">
            Help Center
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto mb-8 text-center">
            Find answers to your questions, explore our guides, or get in touch with our support team.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search for answers..."
                className="pl-12 py-6 text-lg bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Help Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Browse by Category</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {helpCategories.map((category, idx) => (
              <Card key={idx} className="border-2 border-gray-100 hover:border-[#0066FF] hover:shadow-lg transition-all duration-300 cursor-pointer group">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#0066FF]/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#0066FF] transition-colors duration-300">
                    <category.icon className="w-7 h-7 text-[#0066FF] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{category.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                  <span className="text-xs text-[#0066FF] font-medium">{category.articles} articles</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20 border-0 mb-4">
              FAQ
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 text-center">Quick answers to common questions about HRMQore</p>
          </div>

          <div className="space-y-4">
            {filteredFaqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                >
                  <span className="font-medium text-gray-900 pr-4 text-left">{faq.question}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-[#0066FF] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed text-left">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No results found for "{searchQuery}"</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20 border-0 mb-4">
              <MessageSquare className="w-4 h-4 mr-2 inline" />
              Contact Support
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-gray-600 text-center">Send us your question and we&apos;ll get back to you as soon as possible.</p>
          </div>

          <Card className="border-2 border-gray-100">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                  <Input
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Question</label>
                  <textarea
                    placeholder="Describe your question or issue in detail..."
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0066FF] focus:border-transparent resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90 py-6"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Question
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0066FF] to-[#0052CC]">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="text-white">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7" />
              </div>
              <h3 className="font-semibold mb-2">Email Support</h3>
              <p className="text-white/80 text-sm">support@hrmqore.com</p>
              <p className="text-white/60 text-xs mt-1">24-48 hour response</p>
            </div>
            <div className="text-white">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Phone className="w-7 h-7" />
              </div>
              <h3 className="font-semibold mb-2">Phone Support</h3>
              <p className="text-white/80 text-sm">07042050336</p>
              <p className="text-white/60 text-xs mt-1">Mon-Fri, 9AM-5PM WAT</p>
            </div>
            <div className="text-white">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-7 h-7" />
              </div>
              <h3 className="font-semibold mb-2">Live Chat</h3>
              <p className="text-white/80 text-sm">Available on dashboard</p>
              <p className="text-white/60 text-xs mt-1">Business hours only</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;
